import { cx } from '@emotion/css';
import { FieldError, FieldErrorsImpl, Merge, useFormContext } from 'react-hook-form';
import { PropsWithChildren } from 'react';
import { useTranslation } from 'next-i18next';
import { TextArea, TextAreaProps } from '@phoenix/design-system/frontend/ui/form/input/text-area';
import { HintProps, HintType } from '@phoenix/design-system/frontend/ui/form/hint/hint.types';

export const HookFormTextArea = ({
  name,
  className,
  label,
  required = false,
  displayCharCounter = true,
  ...restProps
}: PropsWithChildren<TextAreaProps & { name: string }>) => {
  const { t } = useTranslation('shared');
  const {
    watch,
    register,
    formState: { errors, dirtyFields, touchedFields },
  } = useFormContext();

  const isDirtyOrTouched = (fieldName: string) => dirtyFields[fieldName] || touchedFields[fieldName] || required;

  const getErrorHint = (field?: FieldError | Merge<FieldError, FieldErrorsImpl>): HintProps | undefined => {
    return field
      ? {
          message: field.message,
          type: 'error' as HintType,
        }
      : undefined;
  };

  return (
    <TextArea
      hint={isDirtyOrTouched(name) ? getErrorHint(errors[name]) : undefined}
      className={cx(className, 'align-top', {
        hidden: restProps?.type === 'hidden',
      })}
      label={required ? label : `${label} ${t('shared_formControlLabel_optionalSuffix')}`}
      value={watch(name)}
      displayCharCounter={displayCharCounter}
      {...register(name)}
      {...restProps}
    />
  );
};
